// @ts-ignore
import type * as RCL from 'rclnodejs';

import { namespace, makeNamespacedLog } from '@sb/log';

import { BaseSubscription } from './BaseSubscription';
import { getNode } from './getNode';
import { getRCL } from './getRCL';

const log = makeNamespacedLog('ReachedWaypointsSubscription');
const ns = namespace('ReachedWaypointsSubscription');

/**
 * Authored waypoints the scaled joint trajectory controller has reached in the
 * trajectory it is executing.
 *
 * Two properties of the topic shape how this is consumed:
 *
 *  - `waypoint_indices` is CUMULATIVE, not a delta. Every message repeats every
 *    waypoint crossed so far, so a dropped or coalesced message costs nothing
 *    and handlers must tolerate seeing the same index repeatedly.
 *  - `correlation_id` names ONE trajectory. The controller serves every client
 *    on the robot, so a message carrying any other id belongs to a different
 *    trajectory — a previous iteration, or another client's goal — and must be
 *    ignored outright rather than merged.
 *
 * Handlers are registered per correlation id, so an unmatched message is
 * dropped without the caller ever seeing it.
 */
export interface ReachedWaypointsMessage {
  correlation_id: string;
  waypoint_indices: ArrayLike<number> & Iterable<number>;
}

/**
 * The controller that publishes reached waypoints, and therefore the topic
 * namespace they arrive under.
 *
 * On the ROS side this is the `robot_controller` launch argument, which
 * execute_server also reads to find the controller's action server. Both launch
 * files default it to this value. It is duplicated here because nothing on the
 * TypeScript side can read a launch argument — and if the two ever disagree,
 * this subscription silently matches no publisher and no waypoint ever fires.
 * `ReachedWaypointsSubscription.test.ts` pins the two together.
 */
export const REACHED_WAYPOINTS_CONTROLLER =
  'scaled_joint_trajectory_controller';

export class ReachedWaypointsSubscription extends BaseSubscription<ReachedWaypointsMessage> {
  private robotController: string;

  private handlers = new Map<string, (waypointIndices: number[]) => void>();

  public constructor(robotController: string = REACHED_WAYPOINTS_CONTROLLER) {
    super();
    this.robotController = robotController;

    this.addMessageListener(this.handleMessage.bind(this));
  }

  protected getType(): RCL.TypeClass<keyof RCL.MessagesMap> {
    // Cast because rclnodejs's generated MessagesMap is built from whatever ROS
    // interfaces were present when its bindings were generated, and a locally
    // installed rclnodejs lags newly added standard_bots_msgs types. The name is
    // pinned by ReachedWaypointsSubscription.test.ts against the .msg file.
    return 'standard_bots_msgs/msg/ReachedWaypoints' as RCL.TypeClass<
      keyof RCL.MessagesMap
    >;
  }

  /**
   * Root namespace, with no robot id in it. The controller is spawned by
   * `controller_manager` with no namespace of its own — the node is
   * `/scaled_joint_trajectory_controller` — so its `~/reached_waypoints`
   * resolves to the root. No launch file under `standard_bots_bringup` passes
   * `namespace=`, so this holds on a control box as much as in the dev stack.
   *
   * The robot-id-prefixed hardware topics (`/<robotId>/ro1/hardware/pose`) are
   * not a counter-example: those are published by arm-control-bot with the id
   * built into the topic string, not by a node living in a `/<robotId>`
   * namespace. Prefixing this one produces a topic with no publisher, which
   * fails silently — see the QoS note above for the same failure mode.
   */
  protected getTopic(): string {
    return `/${this.robotController}/reached_waypoints`;
  }

  /**
   * The topic being listened on. Exposed so a caller that reports firing as
   * unavailable can name the topic it found no publisher on — the one fact that
   * makes a namespace mismatch identifiable from a log line.
   */
  public get topicName(): string {
    return this.getTopic();
  }

  /**
   * Whether the controller is publishing reached waypoints on this topic right
   * now.
   *
   * A subscription with no matching publisher raises nothing anywhere: no
   * error, no warning, no dropped-message count. Callers that switch off their
   * own fallback when they believe reports will arrive have to check that
   * belief against the graph, or a topic-name or QoS mismatch costs them every
   * waypoint with no diagnostic at all.
   *
   * Never throws. Callers ask this on the motion path, where a rejection would
   * fail the motion outright — and every reason this could fail (no node, a
   * shutdown mid-query) is a reason to keep the caller's own fallback armed, so
   * an unanswerable question resolves to `false`.
   */
  public async hasPublisher(): Promise<boolean> {
    try {
      const node = await getNode();

      return node.countPublishers(this.getTopic()) > 0;
    } catch (error) {
      log.warn(
        ns`hasPublisher`,
        'Could not count publishers on the reached waypoints topic; treating it as absent',
        { topic: this.getTopic(), error },
      );

      return false;
    }
  }

  /**
   * Must match the controller's publisher exactly. It publishes reliable +
   * volatile with a depth of 10; a profile mismatch means the subscription
   * never matches and no report is ever delivered — with no error anywhere.
   *
   * Volatile is also why this subscription is started once at routine-runner
   * init rather than per goal: a late joiner gets nothing replayed.
   */
  protected override async getQoS(): Promise<RCL.QoS> {
    const rcl = await getRCL();

    // Set through the constructor: `reliability` and `durability` are getters on
    // the QoS type, so assigning to them does not compile.
    return new rcl.QoS(
      rcl.QoS.HistoryPolicy.RMW_QOS_POLICY_HISTORY_KEEP_LAST,
      10,
      rcl.QoS.ReliabilityPolicy.RMW_QOS_POLICY_RELIABILITY_RELIABLE,
      rcl.QoS.DurabilityPolicy.RMW_QOS_POLICY_DURABILITY_VOLATILE,
    );
  }

  /**
   * Route every reached-waypoints message for one trajectory to `handler`,
   * which receives the whole cumulative list on each one — including indices it
   * has already been given. Returns a function that stops the routing.
   *
   * Indices are in the planner's frame (`profile_index`), not the routine's;
   * callers translate before acting on them.
   */
  public onWaypointReachedMessage(
    correlationId: string,
    handler: (waypointIndices: number[]) => void,
  ): () => void {
    if (this.handlers.has(correlationId)) {
      log.warn(
        ns`onWaypointReachedMessage`,
        'replacing an existing handler for this correlation id',
        { correlationId },
      );
    }

    this.handlers.set(correlationId, handler);

    return () => {
      this.handlers.delete(correlationId);
    };
  }

  private handleMessage(message: ReachedWaypointsMessage): void {
    const correlationId = message?.correlation_id;

    if (!correlationId) {
      return;
    }

    const handler = this.handlers.get(correlationId);

    if (!handler) {
      // Another client's trajectory, or one of ours that has already settled.
      return;
    }

    // The rclnodejs client runs with typed arrays enabled, so a uint32[] field
    // arrives as a Uint32Array — which Array.isArray rejects.
    const indices = message.waypoint_indices;

    if (!Array.isArray(indices) && !ArrayBuffer.isView(indices)) {
      return;
    }

    handler(Array.from(indices, Number));
  }
}
